<template>
  <v-sheet color="white" class="ma-0 pa-0 rounded-lg shadow">
    <v-container ref="bp_container" fluid fill-width class="ma-0 pa-0">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <div
            v-if="!outcomeVar"
            class="display-1 primary--text text--lighten-5 pt-5 mt-5"
            align="center"
          >
            NO VARIABLE SELECTED
          </div>

          <div
            v-else-if="!cohorts || cohorts.length == 0"
            class="display-1 primary--text text--lighten-5 pt-5 mt-5"
            align="center"
          >
            NO STUDY GROUPS SELECTED
          </div>

          <div v-else class="ttWrapper">
            <div
              ref="echartContainer"
              :style="
                'width: 100%; height: 100%; min-height: ' +
                  (maxCohorts * rowHeight + gridTop + gridBottom) +
                  'px;' +
                  ' display: ' +
                  (outcomeVar && cohorts && cohorts.length > 0
                    ? 'block'
                    : 'none')
              "
              :class="{ fullScreenView: showFullPanelView }"
            ></div>

            <div class="tooltipButton">
              <v-container class="ma-0 pa-0">
                <v-row class="ma-0 pa-0">
                  <v-col cols="6">
                    <span class="pr-2">Show:</span>

                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-checkbox
                            v-model="showTooltips"
                            class="my-checkbox pa-0 ma-0"
                            label="tooltips"
                            dense
                            hide-details
                          ></v-checkbox>
                        </span>
                      </template>
                      <span v-if="true">Show tooltips</span>
                    </v-tooltip>

                    <v-tooltip v-if="false" top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-checkbox
                            v-model="showPercentages"
                            class="my-checkbox pa-0 ma-0 pl-3"
                            label="percentages"
                            dense
                            hide-details
                          ></v-checkbox>
                        </span>
                      </template>
                      <span v-if="true">Show percentages</span>
                    </v-tooltip>

                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-checkbox
                            v-model="showBarLabels"
                            class="my-checkbox pa-0 ma-0 pl-3"
                            label="bar labels"
                            dense
                            hide-details
                          ></v-checkbox>
                        </span>
                      </template>
                      <span v-if="true">Show labels on bars</span>
                    </v-tooltip>
                  </v-col>
                  <v-col cols="6" class="pa-0 ma-0 pt-1">
                    <v-select
                      v-model="selectedCount"
                      label="x-axis"
                      :items="[
                        {
                          label: 'Study group percentage',
                          value: 'percentages',
                        },
                        {
                          label: 'Number of subjects',
                          value: 'subject counts',
                        },
                      ]"
                      item-text="label"
                      item-value="value"
                      dense
                      hide-details
                    >
                    </v-select>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <canvas style="display: none;"> </canvas>
  </v-sheet>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/dataExplorer/types';
import { format } from 'd3-format';
import { colors } from '@/utils/colors';
import { sortByCategory } from '@/utils/categorical_vars';
import * as echarts from 'echarts';

export default {
  props: {
    showTitleBar: {
      type: Boolean,
      required: false,
      default: true,
    },
    showFullPanelView: {
      type: Boolean,
      required: false,
      default: false,
    },
    analysis: {
      type: Object,
      required: true,
      default: () => {},
    },
    cohorts: {
      type: Array,
      required: true,
      default: () => [],
    },
    outcomeVar: {
      type: Object,
      default: null,
    },
    minHeight: {
      type: Number,
      required: false,
      default: 400,
    },
    minWidth: {
      type: Number,
      required: false,
      default: 300,
    },
    maxCohorts: {
      type: Number,
      required: false,
      default: 1,
    },
    rowHeight: {
      type: Number,
      required: false,
      default: 100,
    },
    rowPad: {
      type: Number,
      required: false,
      default: 12,
    },
    barPad: {
      type: Number,
      required: false,
      default: 5,
    },
    titleClass: {
      type: String,
      required: false,
      default: '',
    },
    labelFontFamily: {
      type: String,
      required: false,
      default: 'sans-serif',
    },
    labelFontSize: {
      type: Number,
      required: false,
      default: 16,
    },
    maxYAxisLabelFrac: {
      type: Number,
      required: false,
      default: 0.45,
    },
    selectedAnalysisMode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      bgkey: 1,
      resizeCount: 0,
      colors: colors,
      width: 0,
      height: 0,
      graphLabelPrefix: '',
      graphData: [],
      graphCategories: [],
      graphDataUpdated: false,
      graphColorKey: [],
      firstVisit: null,
      lastVisit: null,
      showTooltips: false,
      showPercentages: true,
      selectedCount: 'percentages',
      showBarLabels: true,
      myChart: null,
      myChartContainerWidth: null,
      gridTop: 80,
      gridBottom: 100,
    };
  },
  computed: {
    ...mapState('dataExplorer', {
      collection: state.COLLECTION,
      data: state.DATA,
      screenResize: state.SCREEN_RESIZE,
    }),
    // cohorts are collection-specific
    collection_cohorts() {
      const cch = [];
      const cid = this.collection.id;
      let ccnum = 0;

      this.cohorts.forEach(e => {
        if (e.collection_id === cid) {
          e.index = ccnum;
          ccnum += 1;
          cch.push(e);
        }
      });

      return cch;
    },
  },
  watch: {
    outcomeVar() {
      this.updateVisits();
    },
    maxLabelLen() {
      // change in maxLabelLen means change in left margin
      this.$nextTick(() => {
        this.updateGraphData();
      });
    },
    graphData(gd) {
      if (gd.length > 0) {
        this.graphDataUpdated = true;
        this.makeGraph();
      }
    },
    cohorts() {
      var dom = this.$refs.echartContainer;
      this.$nextTick(() => {
        if (typeof dom == 'undefined') this.initializeGraph();
        this.updateGraphData();
      });
    },
    showTooltips(st) {
      this.analysis.showTooltips = st;
      if (typeof this.option.tooltip != 'undefined') {
        this.option.tooltip.show = st;
        this.myChart.setOption(this.option);
      }
    },
    selectedCount(sc) {
      this.showPercentages = sc === 'percentages';
      this.analysis.showPercentages = this.showPercentages;
      var dom = this.$refs.echartContainer;
      this.$nextTick(() => {
        if (typeof dom == 'undefined') this.initializeGraph();
        this.updateGraphData();
      });
    },
    //    showPercentages(sp) {
    //      this.analysis.showPercentages = sp;
    //      var dom = this.$refs.echartContainer;
    //      this.$nextTick(() => {
    //        if (typeof dom == 'undefined') this.initializeGraph();
    //        this.updateGraphData();
    //      });
    //    },
    showBarLabels(sbl) {
      this.analysis.showBarLabels = sbl;
      var dom = this.$refs.echartContainer;
      this.$nextTick(() => {
        if (typeof dom == 'undefined') this.initializeGraph();
        this.updateGraphData();
      });
    },
    analysis: {
      handler(a) {
        if (a.showTooltips != this.showTooltips)
          this.showTooltips = a.showTooltips;
        let selectedCount = a.showPercentages
          ? 'percentages'
          : 'subject counts';
        if (selectedCount != this.selectedCount)
          this.selectedCount = selectedCount;
      },
      deep: true,
    },
  },
  mounted() {
    this.showTooltips = this.analysis.showTooltips;
    this.selectedCount = this.analysis.showPercentages
      ? 'percentages'
      : 'subject counts';
    this.graphDataUpdated = false;
    this.updateVisits();
    this.initializeGraph();
  },
  methods: {
    onResizeHandler(count) {
      if (count == this.resizeCount) {
        this.resizeCount = 0;
        this.$nextTick(() => {
          this.myChart.resize();
          this.updateGraphData();
        });
      }
    },
    onResize() {
      let rc = ++this.resizeCount;
      setTimeout(() => this.onResizeHandler(rc), 500);
    },
    initializeGraph() {
      var dom = this.$refs.echartContainer;
      this.myChart = echarts.init(dom, null, {
        renderer: 'canvas',
        useDirtyRect: false,
      });
      this.makeGraph();
      const resizeObserver = new ResizeObserver(() => {
        this.myChartContainerWidth = dom.offsetWidth;
        this.option.grid = this.getGrid();
        this.myChart.setOption(this.option);
        this.myChart.resize();
      });
      resizeObserver.observe(dom);
    },
    // recompute grids based on current width
    getGrid() {
      // compute exact length of longest label (if possible)
      var maxTitleLengthPx = 0;
      const canvases = document.getElementsByTagName('canvas');

      if (canvases.length == 0) {
        maxTitleLengthPx = 200;
      } else {
        const ctx = canvases[0].getContext('2d');
        ctx.font = this.labelFontSize + 'px ' + this.labelFontFamily;

        this.cohorts.forEach(c => {
          let label = this.graphLabelPrefix + c.label.replace(/^\[O\]_/, '');
          let mt = ctx.measureText(label);
          if (mt.width > maxTitleLengthPx) {
            maxTitleLengthPx = mt.width + 30;
          }
        });
      }

      // use at most maxYAxisLabelFrac of the available space for the y-axis captions
      if (this.myChartContainerWidth != null) {
        const maxTitleWidthPx =
          this.maxYAxisLabelFrac * this.myChartContainerWidth;
        if (maxTitleLengthPx > maxTitleWidthPx) {
          maxTitleLengthPx = maxTitleWidthPx;
        }
      }

      return [
        // middle grid - the graph itself
        {
          left: maxTitleLengthPx,
          right: 20,
          bottom: this.gridBottom,
          top: this.gridTop,
        },
        // left grid - y-axis label space
        {
          show: true,
          left: 0,
          top: this.gridTop,
          bottom: this.gridBottom,
          width: maxTitleLengthPx,
          borderWidth: 0,
          zlevel: 1,
        },
      ];
    },
    makeGraph() {
      let gdata = this.graphData;
      let gcats = this.graphCategories;
      let showPct = this.showPercentages;
      let prefix = this.graphLabelPrefix;
      let notChange = !prefix.match(/^change/i);

      let xFmt = function(value) {
        if (showPct) {
          if (notChange) return value + '%';
          return value > 0 ? '+' + value + '%' : value + '%';
        } else {
          if (notChange) return value;
          return value > 0 ? '+' + value : value;
        }
      };

      this.option = {
        title: [
          {
            text:
              this.outcomeVar.abbreviation === this.outcomeVar.label
                ? this.outcomeVar.abbreviation
                : this.outcomeVar.abbreviation + ' | ' + this.outcomeVar.label,
            left: 'center',
            show: this.showTitleBar,
          },
        ],
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'shadow',
          },
          show: this.showTooltips,
          valueFormatter: x => xFmt(x),
        },
        grid: this.getGrid(),
        xAxis: {
          show: true,
          type: 'value',
          scale: true,
          min: 0,
          // force max to 100 for non-change percentages - otherwise it tends to display 120% as the x-axis max
          max: function(value) {
            return notChange && showPct ? 100.0 : value.max;
          },
          axisLabel: { fontSize: this.labelFontSize, formatter: x => xFmt(x) },
          name: this.showPercentages
            ? notChange
              ? 'Study group percentage'
              : 'Change in study group percentage'
            : notChange
            ? 'Number of subjects'
            : 'Change in number of subjects',
          nameLocation: 'middle',
          nameGap: 35,
          zlevel: 1,
        },
        yAxis: {
          type: 'category',
          data: this.cohorts.map(
            c => this.graphLabelPrefix + c.label.replace(/^\[O\]_/, '')
          ),
          inverse: true,
          axisLabel: {
            fontSize: this.labelFontSize,
            fontFamily: this.labelFontFamily,
            overflow: 'truncate',
            align: 'right',
          },
        },
        series: gdata,
        textStyle: {
          fontSize: this.labelFontSize,
          fontFamily: this.labelFontFamily,
          color: '#000000',
        },
        legend: {
          data: gcats.map(gc => {
            return { name: gc };
          }),
          selected: { detail: false },
          top: 50,
          left: 'center',
          type: 'scroll',
        },
      };
      // see https://github.com/apache/echarts/issues/6202
      this.myChart.setOption(this.option, true);
    },
    updateVisits() {
      var bp = this;
      if (this.outcomeVar == null) return;
      this.collection.observation_variables_list.forEach(v => {
        if (v.ontology.id == bp.outcomeVar.id) {
          if (v.first_visit_event != null) {
            bp.firstVisit = v.first_visit_event;
            bp.lastVisit = v.last_visit_event;
          } else if (v.first_visit_num != null) {
            bp.firstVisit = v.first_visit_num;
            bp.lastVisit = v.last_visit_num;
          }
        }
      });
      this.$nextTick(() => {
        this.updateGraphData();
      });
    },

    getCounts(data) {
      var counts = {};
      data.forEach(x => {
        if (x != null) {
          if (!(x in counts)) {
            counts[x] = 0;
          }
          counts[x] += 1;
        }
      });
      return counts;
    },

    // visit = firstVisit, lastVisit, change, or value
    updateGraphData_aux(visit, label_prefix) {
      this.graphLabelPrefix = label_prefix;
      let allCats = {};
      let cohortCounts = [];

      this.cohorts.forEach(c => {
        const subjids = [];
        c.subject_ids.forEach(sid => {
          subjids[sid] = 1;
        });

        const subjData = this.data.filter(d => d.subject_id in subjids);
        var counts = {};
        var total = subjData.length;

        if (this.outcomeVar.type == 'observation') {
          if (visit == 'change') {
            const firstData = subjData.map(
              x => x[this.outcomeVar.id]['firstVisit']
            );
            const lastData = subjData.map(
              x => x[this.outcomeVar.id]['lastVisit']
            );
            var firstCounts = this.getCounts(firstData);
            var lastCounts = this.getCounts(lastData);

            Object.keys(firstCounts).forEach(k => {
              allCats[k] = 1;
            });
            Object.keys(lastCounts).forEach(k => {
              allCats[k] = 1;
            });
            Object.keys(allCats).forEach(c => {
              let firstCount = c in firstCounts ? firstCounts[c] : 0;
              let lastCount = c in lastCounts ? lastCounts[c] : 0;
              let firstPct = (firstCount / total) * 100.0;
              let lastPct = (lastCount / total) * 100.0;
              let pctDiff = Number(lastPct - firstPct).toFixed(1);
              counts[c] = this.showPercentages
                ? pctDiff
                : lastCount - firstCount;
            });
          } else {
            // visit != 'change'
            const cohortData = subjData.map(x => x[this.outcomeVar.id][visit]);
            counts = this.getCounts(cohortData);
            // convert to percentages
            if (this.showPercentages) {
              let cats = Object.keys(counts);
              cats.forEach(c => {
                counts[c] = Number((counts[c] / total) * 100.0).toFixed(1);
              });
            }

            Object.keys(counts).forEach(k => {
              allCats[k] = 1;
            });
          }
        } else if (
          this.selectedAnalysisMode == 'Outcome based' &&
          this.outcomeVar.type == 'subject'
        ) {
          var cohortData = [];
          if (this.outcomeVar.category == 'Dataset') {
            cohortData = subjData.map(x => x.study.study_name);
          } else {
            cohortData = subjData.map(x => x[this.outcomeVar.label]);
          }

          counts = this.getCounts(cohortData);
          // convert to percentages
          if (this.showPercentages) {
            let cats = Object.keys(counts);
            cats.forEach(c => {
              counts[c] = Number((counts[c] / total) * 100.0).toFixed(1);
            });
          }

          Object.keys(counts).forEach(k => {
            allCats[k] = 1;
          });
        } else {
          return;
        }

        cohortCounts.push(counts);
      });

      // group data by sorted category
      let sortedCats = sortByCategory(
        this.outcomeVar,
        Object.keys(allCats),
        a => a
      );
      let showPct = this.showPercentages;
      let showBarLabels = this.showBarLabels;
      let gData = [];
      // format stacked bar labels
      let lblFmtFn = function(o) {
        if (!showBarLabels) return '';
        if (showPct) {
          return (
            (visit == 'change' && o.value > 0 ? '+' : '') +
            format('.1f')(o.value) +
            '%'
          );
        } else {
          return (visit == 'change' && o.value > 0 ? '+' : '') + o.value;
        }
      };

      sortedCats.forEach(gc => {
        let ccs = cohortCounts.map(cc => cc[gc]);

        gData.push({
          name: gc,
          type: 'bar',
          stack: 'total',
          label: { show: true, formatter: lblFmtFn },
          data: ccs,
        });
      });

      this.graphData = gData;
      this.graphCategories = sortedCats;
    },

    updateGraphData() {
      if (!this.outcomeVar) return;

      this.graphData = [];
      this.graphColorKey = [];

      var comparisonMeasure = this.analysis.input.comparisonField;

      if (this.outcomeVar.type == 'observation') {
        if (this.outcomeVar.is_longitudinal) {
          // use percentages by default
          if (comparisonMeasure === 'firstVisit') {
            this.updateGraphData_aux('firstVisit', this.firstVisit + ' | ');
          } else if (comparisonMeasure === 'lastVisit') {
            this.updateGraphData_aux('lastVisit', this.lastVisit + ' | ');
          } else if (comparisonMeasure === 'change') {
            this.updateGraphData_aux('change', 'Change' + ' | ');
          }
        }
        // cross-sectional data
        else {
          this.updateGraphData_aux('value', '');
        }
      } else if (
        this.selectedAnalysisMode == 'Outcome based' &&
        this.outcomeVar.type == 'subject'
      ) {
        this.updateGraphData_aux('', '');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ttWrapper {
  position: relative;
}

.tooltipButton {
  position: absolute;
  top: 0rem;
  left: 1rem;
  margin: auto;
}

.fullScreenView {
  left: 0% !important;
  margin-top: 2rem;
}

.my-checkbox {
  display: inline-block;
}

::v-deep .my-checkbox .v-label {
  font-size: 15px;
}
</style>
